import React from "react";
import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { Link } from "react-router-dom";

const DriverDetailsTable = ({ getDriverOrderId = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const renderDriverAcceptanceChip = (item) => {
    if (item?.driver_acceptance === 1 && item?.picked_up === 0) {
      return <Chip label="Accepted" color="success" size="small" />;
    } else if (item?.driver_acceptance === 1 && item?.picked_up === 1) {
      return (
        <Chip
          label="On the way"
          size="small"
          sx={{
            background: "orange",
            color: "#fff",
          }}
        />
      );
    } else {
      return <Chip label="Delivered" color="secondary" size="small" />;
    }
  };

  // ==========================
  // Mobile View
  // ==========================
  if (isMobile) {
    return (
      <div className="my-3">
        <h5 className="text-primary">Delivery Team Details</h5>

        <Stack spacing={2}>
          {getDriverOrderId.map((item, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 2,
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="primary"
                  >
                    Driver #{index + 1}
                  </Typography>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Region
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Typography variant="body2">
                    {item?.driver?.city?.name || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Driver
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Link
                    to={`/view-driver-order/${item?.driver?.driver_id}/${item?.order_id}`}
                    replace
                    style={{
                      textDecoration: "none",
                    }}
                  >
                    {item?.driver?.driver_name}
                  </Link>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Mobile
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Typography variant="body2">
                    {item?.driver?.driver_mobile}
                  </Typography>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Acceptance
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  {item?.driver_acceptance === 1 ? (
                    <Chip
                      label="Accepted"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      label="Pending"
                      size="small"
                      sx={{
                        background: "orange",
                        color: "#fff",
                      }}
                    />
                  )}
                </Grid>

                {item?.driver_uploaded_image && (
                  <>
                    <Grid item xs={5}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        Delivered Image
                      </Typography>
                    </Grid>

                    <Grid item xs={7}>
                      <PhotoProvider>
                        <PhotoView src={item?.driver_uploaded_image}>
                          <img
                            src={item?.driver_uploaded_image}
                            alt="Delivered"
                            style={{
                              width: 90,
                              height: 90,
                              objectFit: "cover",
                              borderRadius: 8,
                              cursor: "pointer",
                            }}
                          />
                        </PhotoView>
                      </PhotoProvider>
                    </Grid>

                    <Grid item xs={5}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        Delivery
                      </Typography>
                    </Grid>

                    <Grid item xs={7}>
                      <span className="text-info status_icon">
                        <i
                          className="fa fa-smile-o mr-2 mobile-icon"
                          aria-hidden="true"
                        ></i>
                        Delivered
                      </span>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          ))}
        </Stack>
      </div>
    );
  }

  // ==========================
  // Desktop View (UNCHANGED)
  // ==========================
  return (
    <div className="my-3">
      <h5 className="text-primary">Delivery Team Details</h5>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="driver details table">
          <TableHead>
            <TableRow>
              <TableCell align="center">S.No</TableCell>
              <TableCell align="center">Region</TableCell>
              <TableCell align="center">Driver Name</TableCell>
              <TableCell align="center">Driver Mobile Number</TableCell>
              <TableCell align="center">Acceptance Status</TableCell>

              {getDriverOrderId[0]?.driver_uploaded_image && (
                <>
                  <TableCell align="center">Delivered Image</TableCell>
                  <TableCell align="center">Delivery Status</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {getDriverOrderId.map((item, index) => (
              <TableRow key={index}>
                <TableCell align="center">{index + 1}</TableCell>

                <TableCell align="center">
                  {item?.driver?.city?.name}
                </TableCell>

                <TableCell align="center">
                  <Link
                    to={`/view-driver-order/${item?.driver?.driver_id}/${item?.order_id}`}
                    replace
                  >
                    {item?.driver?.driver_name}
                  </Link>
                </TableCell>

                <TableCell align="center">
                  {item?.driver?.driver_mobile}
                </TableCell>

                <TableCell align="center">
                  {item?.driver_acceptance === 1 ? (
                    <Chip label="Accepted" color="success" />
                  ) : (
                    <Chip
                      label="Pending"
                      sx={{
                        background: "orange",
                        color: "white",
                      }}
                    />
                  )}
                </TableCell>

                {item?.driver_uploaded_image && (
                  <>
                    <TableCell align="center">
                      <PhotoProvider>
                        <PhotoView src={item?.driver_uploaded_image}>
                          <img
                            src={item?.driver_uploaded_image}
                            alt="Delivered"
                            className="product-img"
                            style={{
                              cursor: "pointer",
                              width: "100px",
                              borderRadius: "8px",
                            }}
                          />
                        </PhotoView>
                      </PhotoProvider>
                    </TableCell>

                    <TableCell align="center">
                      <span className="text-info status_icon m-1">
                        <i
                          className="fa fa-smile-o mr-2 mobile-icon"
                          aria-hidden="true"
                        ></i>

                        <span className="window-text">
                          Delivered
                        </span>
                      </span>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default DriverDetailsTable;